import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Select, TextEditor, TextInput } from 'components/Form'
import { createApiRecord, updateApiRecord } from 'services/CompanyService'
import { useDispatch, useSelector } from 'react-redux'
import { getRecord } from './store/dataSlice'
import reducer from './store'
import { injectReducer } from 'store'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { Success } from 'utils/alerts'
import { getCompanyData } from 'store/data/dataSlice'
import { yesNoOptionsString } from 'utils/options'
injectReducer('company', reducer)

const validationSchema = Yup.object().shape({
    name: Yup.string().required('company name is required'),
    phone: Yup.number()
        .required('Contact number is required')
        .max(999999999999, 'Contact number must be between 7 to 12 digits')
        .min(9999999, 'Contact number must be between 7 to 12 digits'),
    company_email: Yup.string()
        .required('email is required')
        .email('Email is invalid')
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, 'Email is invalid'),
    address: Yup.string().required('address is required'),
    country: Yup.string().required('country is required'),
    city: Yup.string().required('city is required'),
    pin: Yup.number().required('city is required'),
    send_mail: Yup.string().required('send mail is required'),
})

const Create = props => {
    const { initialData } = props
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { record, loading } = useSelector(state => state.company.data)

    useEffect(() => {
        if (id) {
            fetchData(id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const fetchData = id => dispatch(getRecord(id))

    const handleDiscard = () => navigate('/company')

    const handleSubmit = data => {
        if (id) {
            updateApiRecord(id, data)
                .then(() => {
                    Success('Updated Successfully!')
                    handleDiscard()
                    dispatch(getCompanyData())
                })
                .catch(error => console.log('Error: ', error))
        } else {
            createApiRecord(data)
                .then(() => {
                    Success('Created Successfully!')
                    handleDiscard()
                    dispatch(getCompanyData())
                })
                .catch(error => console.log('Error: ', error))
        }
    }

    if (loading && id) {
        return null
    }

    return (
        <>
            <div className="flex flex-col gap-1 mb-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">Companies</h2>
            </div>
            <div className="flex flex-col items-center">
                <div className="w-full bg-white border rounded-sm border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                    <div className="px-5 py-3 border-b border-stroke dark:border-strokedark">
                        <h3 className="font-medium text-black dark:text-white">{id ? 'Update Company' : 'Add New Company'}</h3>
                    </div>
                    <div className="flex flex-col gap-3 p-6">
                        <Formik
                            initialValues={id ? record : initialData}
                            validationSchema={validationSchema}
                            onSubmit={values => {
                                handleSubmit(values)
                            }}
                        >
                            {({ values, touched, errors }) => (
                                <Form>
                                    <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-4">
                                        <Field name="name">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Company Name"
                                                    name="name"
                                                    error={errors.name && touched.name}
                                                    message={errors.name}
                                                    value={values?.name}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                />
                                            )}
                                        </Field>
                                        <Field name="country">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Country"
                                                    name="country"
                                                    error={errors.country && touched.country}
                                                    message={errors.country}
                                                    value={values?.country}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                />
                                            )}
                                        </Field>
                                        <Field name="company_email">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Email"
                                                    name="company_email"
                                                    error={errors.company_email && touched.company_email}
                                                    message={errors.company_email}
                                                    value={values?.company_email}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                />
                                            )}
                                        </Field>
                                        <Field name="city">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="City"
                                                    name="city"
                                                    error={errors.city && touched.city}
                                                    message={errors.city}
                                                    value={values?.city}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                />
                                            )}
                                        </Field>
                                        <Field name="phone">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Contact Number"
                                                    name="phone"
                                                    error={errors.phone && touched.phone}
                                                    message={errors.phone}
                                                    value={values?.phone}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                />
                                            )}
                                        </Field>
                                        <Field name="address">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Address"
                                                    name="address"
                                                    error={errors.address && touched.address}
                                                    message={errors.address}
                                                    value={values?.address}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                />
                                            )}
                                        </Field>
                                        <Field name="pin">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Pincode"
                                                    name="pin"
                                                    error={errors.pin && touched.pin}
                                                    message={errors.pin}
                                                    value={values?.pin}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                />
                                            )}
                                        </Field>
                                        <Field name="send_mail">
                                            {({ field, form }) => (
                                                <Select
                                                    options={yesNoOptionsString}
                                                    label="Send Mail"
                                                    size="sm"
                                                    placeholder="Select"
                                                    className="md:col-span-2"
                                                    name="send_mail"
                                                    error={errors.send_mail && touched.send_mail}
                                                    message={errors.send_mail}
                                                    value={values?.send_mail}
                                                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                />
                                            )}
                                        </Field>
                                        <Field name="terms_and_conditions">
                                            {({ field, form }) => (
                                                <TextEditor
                                                    className="md:col-span-4"
                                                    label="Terms and Conditions"
                                                    name="terms_and_conditions"
                                                    error={errors.terms_and_conditions && touched.terms_and_conditions}
                                                    message={errors.terms_and_conditions}
                                                    value={values?.terms_and_conditions}
                                                    onChange={e => form.setFieldValue(field.name, e)}
                                                />
                                            )}
                                        </Field>
                                    </div>
                                    <div className="flex items-center justify-end gap-4 mt-3">
                                        <Button
                                            className="h-10 w-18 text-primary border-primary dark:text-secondary dark:border-secondary"
                                            onClick={handleDiscard}
                                        >
                                            Discard
                                        </Button>
                                        <Button
                                            type="submit"
                                            className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-primary bg-primary dark:border-secondary dark:bg-secondary"
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    )
}

Create.defaultProps = {
    initialData: {
        name: '',
        phone: '',
        company_email: '',
        address: '',
        country: '',
        city: '',
        pin: '',
        terms_and_conditions: '',
        send_mail: '',
    },
}

export default Create
